import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useTacticHooks } from '../../providers/TacticHooksProvider';
import { WeekDayNames } from '@gettactic/api';

export const defaultWeekDays: WeekDayNames[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

export const defaultWeekDaysCount = defaultWeekDays.length;

export function useAvailableDays(
  office?: { id: string },
  options?: Omit<
    UseQueryOptions<WeekDayNames[], unknown, WeekDayNames[], (string | undefined)[]>,
    'queryFn' | 'queryKey'
  >
) {
  const { api } = useTacticHooks();
  return useQuery({
    // Must differ from useOperationalHours' key: both fetch the same endpoint
    // but cache different shapes. The "hours.rules" prefix keeps it covered by
    // the existing hours.rules invalidations.
    queryKey: ['hours.rules', office?.id, 'availableDays'],

    queryFn: async () => {
      const res = await api.client.hours.weekdays(office?.id ?? '');

      if (!res.ok) {
        console.warn(`Failed to fetch available days for office ${office?.id}:`, res.error);
        // most orgs work only 5 days a week
        return defaultWeekDays;
      }
    
      // Check if res.result is the raw API response object (has elements, cursor, total)
      const elements = res.result?.elements || [];
      // if (!elements && res.result && typeof res.result === 'object' && 'elements' in res.result) {
      //   // This is the raw API response, extract elements
      //   elements = res.result.elements;
      //   console.warn('useAvailableDays: Detected raw API response, extracting elements:', elements);
      // }
      
      const availableDays = (elements || [])
        .filter((day) => day.type === 'weekday' && day.is_available)
        .reduce((acc, day) => {
          if (day.days) {
            day.days.forEach((day) => {
              if (!acc.includes(day)) {
                acc.push(day);
              }
            });
          }
          return acc;
        }, [] as WeekDayNames[])
        .sort(
          (a, b) =>
            ALL_WEEK_DAYS[a as WeekDayNames] - ALL_WEEK_DAYS[b as WeekDayNames]
        );
      
      // Ensure we're returning an array of strings (WeekDayNames)
      if (!Array.isArray(availableDays) || !availableDays.length) {
        console.warn(`useAvailableDays: Expected array but got ${typeof availableDays}, using defaults`);
        return defaultWeekDays;
      }
      
      // Validate that all items are valid WeekDayNames
      const validDays = availableDays.filter(day => 
        typeof day === 'string' && Object.prototype.hasOwnProperty.call(ALL_WEEK_DAYS, day)
      );
      
      if (validDays.length !== availableDays.length) {
        console.warn('useAvailableDays: Some invalid days found, filtering to valid ones:', validDays);
        return validDays.length > 0 ? validDays : defaultWeekDays;
      }
      
      return availableDays;
    },

    enabled: !!office?.id && options?.enabled,
    ...options
  });
}

// Check which day user starts with
export const ALL_WEEK_DAYS: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6
};
